import { useQueryClient } from "@tanstack/react-query";
import PQueue from "p-queue";
import { useEffect, useState } from "react";
import {
  getListDocumentsQueryKey,
  useCreateUpload,
} from "~/api/default/default";
import {
  useCompleteMultipartUpload,
  useGetMultipartPartUrl,
  useStartMultipartUpload,
} from "~/api/documents/documents";
import { Document } from "~/api/model";
import { getGetOrganizationQueryKey } from "~/api/organizations/organizations";
import { toast } from "~/hooks/use-toast";
import { uploadChunkToS3 } from "~/lib/s3";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";

import { FileWithHash } from "~/features/case/hooks/useDocumentsList.ts";

const MAX_CONCURRENT_CHUNKS = 6;
const POLLING_INTERVAL_MS = 10000;
const CHUNK_SIZE = 6 * 1024 * 1024; // 6MB - AWS S3 requires minimum 5MB for multipart uploads (except last part)

interface UseDocumentUploadParams {
  organizationId: string;
  caseId: string;
  sessionBatchKey: string;
}
let uploadProgressLocal: Record<string, number> = {};
export const useDocumentUpload = ({
  organizationId,
  caseId,
  sessionBatchKey,
}: UseDocumentUploadParams) => {
  const { mutateAsync: createUpload } = useCreateUpload();
  const { mutateAsync: startMultipartUpload } = useStartMultipartUpload();
  const { mutateAsync: getMultipartPartUrl } = useGetMultipartPartUrl();
  const { mutateAsync: completeMultipartUpload } = useCompleteMultipartUpload();
  const queryClient = useQueryClient();

  const [uploadQueue] = useState(
    () => new PQueue({ concurrency: MAX_CONCURRENT_CHUNKS })
  );

  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );

  const [queuedDocuments, setQueuedDocuments] = useState<
    (Document & { uploadId?: string })[]
  >([]);

  const [processingDocuments, setProcessingDocuments] = useState<Set<string>>(
    new Set()
  );

  const [abortControllersByFilename] = useState<
    Record<string, AbortController>
  >({});

  const handleCreateDocument = async (file: FileWithHash): Promise<void> => {
    const uploadStartTime = Date.now();

    try {
      const payload = {
        filename: file.name,
        contentType: file.type,
        batchKey: sessionBatchKey,
      };

      uploadProgressLocal[file.fileHash] = 10;
      logger.info("🔄 UPLOAD PAYLOAD", payload, uploadProgressLocal);
      // Set initial progress
      setUploadProgress({ ...uploadProgressLocal });
      const upload = await createUpload({
        organizationId,
        projectId: caseId,
        data: payload,
      });
      // Replace queued document with uploadId
      setQueuedDocuments((prev) =>
        prev.map((doc) =>
          doc.fileHash === file.fileHash ? { ...doc, uploadId: upload.id } : doc
        )
      );

      const startResp = await startMultipartUpload({
        organizationId,
        projectId: caseId,
        uploadId: upload.id,
        data: {
          contentType: file.type,
          filename: file.name,
        },
      });

      const { uploadId, key } = startResp;

      const chunkCount = Math.ceil(file.size / CHUNK_SIZE);
      const uploadedParts: { ETag: string; PartNumber: number }[] = [];

      // Prepare abort controller for this file if not present
      if (!abortControllersByFilename[file.fileHash]) {
        abortControllersByFilename[file.fileHash] = new AbortController();
      }
      const abortSignal = abortControllersByFilename[file.fileHash].signal;

      const uploadChunk = async (index: number, progressPct: number) => {
        if (abortSignal.aborted) {
          throw new DOMException("Aborted", "AbortError");
        }
        const partNumber = index + 1;
        const start = index * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const partUrlData = await getMultipartPartUrl({
          organizationId,
          projectId: caseId,
          uploadId: upload.id,
          data: {
            key,
            uploadId,
            partNumber,
          },
        });
        const etagHeader = await uploadChunkToS3(
          partUrlData.signedUrl as string,
          chunk,
          file.type,
          (progress) => {
            uploadProgressLocal[file.fileHash] =
              uploadProgressLocal[file.fileHash] + progress * progressPct;
            setUploadProgress({ ...uploadProgressLocal });
          },
          abortSignal
        );
        if (!etagHeader) {
          throw new Error("Missing ETag in S3 response");
        }

        uploadedParts.push({
          ETag: etagHeader.replace(/"/g, ""),
          PartNumber: partNumber,
        });
      };

      // Queue each chunk individually through PQueue
      const chunkPromises: Promise<void>[] = [];
      for (let i = 0; i < chunkCount; i++) {
        chunkPromises.push(
          uploadQueue.add(() => uploadChunk(i, 90 / chunkCount), {
            priority: 0,
          })
        );
      }
      await Promise.all(chunkPromises);

      await completeMultipartUpload({
        organizationId,
        projectId: caseId,
        uploadId: upload.id,
        data: {
          fileHash: file.fileHash,
          key,
          uploadId,
          parts: uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber),
        },
      });
      uploadProgressLocal[file.fileHash] = 100;
      setUploadProgress({ ...uploadProgressLocal });
      setProcessingDocuments((prev) => new Set(prev).add(file.fileHash));

      const uploadDurationMs = Date.now() - uploadStartTime;
      trackEvent({
        eventName: "app/case/upload.process/file_completed",
        properties: {
          filename: file.name,
          fileSizeBytes: file.size,
          uploadDurationMs,
          caseId,
        },
      });

      // Cleanup abort controller after successful upload
      delete abortControllersByFilename[file.fileHash];
    } catch (error) {
      // If aborted intentionally, do not log as error nor mark failed
      if (error instanceof DOMException && error.name === "AbortError") {
        logger.info(`⏹️ Upload aborted for ${file.name}`);
      } else {
        logger.error(`❌ Error uploading document ${file.name}:`, error);

        trackEvent({
          eventName: "app/case/upload.process/file_failed",
          properties: {
            filename: file.name,
            fileSizeBytes: file.size,
            errorMessage:
              error instanceof Error ? error.message : String(error),
            caseId,
          },
        });

        toast({
          variant: "destructive",
          title: "Upload failed",
          description: `Failed to upload "${file.name}". Please try again.`,
        });
      }
      // Remove from queued documents and processing on error
      setQueuedDocuments((prev) =>
        prev.filter((doc) => doc.fileHash !== file.fileHash)
      );
      if (error && typeof error === "object" && "message" in error) {
        const errorMessage = (error as { message: string }).message || "";
        if (
          errorMessage.includes("Free trial limit reached") ||
          errorMessage.includes("Payment required")
        ) {
          queryClient.invalidateQueries({
            queryKey: getGetOrganizationQueryKey(organizationId),
          });
        }
      }
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        logger.info("Set progress on error", uploadProgress);
        setUploadProgress((prev) => ({ ...prev, [file.fileHash]: -1 }));
        throw error;
      }
    }
  };

  const hasActiveUploads = uploadQueue.size > 0 || uploadQueue.pending > 0;
  // const hasActiveUploads = Object.values(uploadProgressLocal).reduce((sum, cur) => {
  //   return sum + cur == 100 ? 1 : 0
  // }, 0) > 0;

  // Has documents waiting to appear in the API (with uploadId)
  const hasWaitingDocuments = queuedDocuments.some((doc) => doc.uploadId);

  // Polling effect for invalidating queries while uploads are active
  useEffect(() => {
    let isCancelled = false;

    const poll = async () => {
      return;
      if (isCancelled) {
        return;
      }

      if (!hasActiveUploads && !hasWaitingDocuments) {
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: getListDocumentsQueryKey(organizationId, caseId),
      });

      setTimeout(poll, POLLING_INTERVAL_MS);
    };

    poll();

    return () => {
      isCancelled = true;
    };
  }, [
    hasActiveUploads,
    hasWaitingDocuments,
    caseId,
    organizationId,
    queryClient,
  ]);

  const uploadDocuments = async (files: FileWithHash[]) => {
    const batchStartTime = Date.now();
    const totalSizeBytes = files.reduce((sum, file) => sum + file.size, 0);
    files.map((file) => (uploadProgressLocal[file.fileHash] = 10));
    setUploadProgress({ ...uploadProgressLocal });

    trackEvent({
      eventName: "app/case/upload.process/started",
      properties: {
        fileCount: files.length,
        totalSizeBytes,
        caseId,
        organizationId,
        batchKey: sessionBatchKey,
      },
    });

    let successCount = 0;
    let failedCount = 0;

    try {
      const results = await Promise.allSettled(files.map(handleCreateDocument));
      const activeUploads =
        Object.values(uploadProgress).reduce((sum, cur) => {
          return sum + cur == 100 ? 0 : 1;
        }, 0) > 0;
      if (!activeUploads) {
        uploadProgressLocal = {};
        setUploadProgress({ ...uploadProgressLocal });
        //Reload documents list
        await queryClient.invalidateQueries({
          queryKey: getListDocumentsQueryKey(organizationId, caseId),
        });
      }
      results.forEach((result) => {
        if (result.status === "fulfilled") {
          successCount++;
        } else {
          failedCount++;
        }
      });
    } catch (error) {
      logger.error("Upload batch encountered errors", error as Error);
    }

    const totalDurationMs = Date.now() - batchStartTime;

    trackEvent({
      eventName: "app/case/upload.process/batch_completed",
      properties: {
        fileCount: files.length,
        successCount,
        failedCount,
        totalSizeBytes,
        totalDurationMs,
        caseId,
      },
    });

    return null;
  };

  const cancelDraftUpload = (filename: string) => {
    const currentProgress = uploadProgress[filename] || 0;

    trackEvent({
      eventName: "app/case/upload.process/file_cancelled",
      properties: {
        filename,
        progressPercent: currentProgress,
        caseId,
      },
    });

    const controller = abortControllersByFilename[filename];
    if (controller) {
      controller.abort();
      delete abortControllersByFilename[filename];
    }

    setQueuedDocuments((prev) =>
      prev.filter((doc) => doc.filename !== filename)
    );
    setProcessingDocuments((prev) => {
      const next = new Set(prev);
      next.delete(filename);
      return next;
    });
    logger.info("Cancel draft upload", uploadProgress);
    setUploadProgress((prev) => {
      const next = { ...prev };
      delete next[filename];
      return next;
    });
  };

  return {
    uploadDocuments,
    cancelDraftUpload,
    uploadProgress,
    queuedDocuments,
    setQueuedDocuments,
    processingDocuments,
    setProcessingDocuments,
    setUploadProgress,
    hasActiveUploads,
    hasWaitingDocuments,
    pendingUploads: uploadQueue.size,
    activeUploads: uploadQueue.pending,
  };
};
